import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONTINENTS = ["North America", "South America", "Europe", "Asia", "Africa", "Oceania"];

const TIER_COLORS = {
  1: "bg-amber-500/20 text-amber-400 border-amber-500/40",
  2: "bg-cyan-500/20 text-cyan-400 border-cyan-500/40",
  3: "bg-purple-500/20 text-purple-400 border-purple-500/40",
};

const TIER_LABELS = { 1: "Iconic", 2: "Regional", 3: "Hidden Gem" };

const PRIORITY_COLORS = {
  High: "bg-red-500/20 text-red-400",
  Medium: "bg-amber-500/20 text-amber-400",
  Low: "bg-slate-500/20 text-slate-400",
};

export function AICuration() {
  // Step state
  const [step, setStep] = useState("select"); // select | regions | generating | beaches
  const [selectedContinent, setSelectedContinent] = useState("");
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState("");

  // Regions
  const [regions, setRegions] = useState([]);
  const [isGeneratingRegions, setIsGeneratingRegions] = useState(false);
  const [selectedRegions, setSelectedRegions] = useState([]);
  const [regionsCached, setRegionsCached] = useState(false);

  // Beach generation
  const [isGeneratingBeaches, setIsGeneratingBeaches] = useState(false);
  const [generationProgress, setGenerationProgress] = useState({ current: 0, total: 0, currentName: "" });

  // Browse state
  const [curatedBeaches, setCuratedBeaches] = useState([]);
  const [beachTotal, setBeachTotal] = useState(0);
  const [filterTier, setFilterTier] = useState("");
  const [filterTag, setFilterTag] = useState("");
  const [filterRegion, setFilterRegion] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);

  // Stats
  const [curatedStats, setCuratedStats] = useState({ total_beaches: 0, total_regions: 0, total_countries: 0, by_tier: {}, by_country: {} });

  // Fetch countries
  useEffect(() => {
    if (!selectedContinent) return;
    axios.get(`${API}/countries/${selectedContinent}`)
      .then((res) => setCountries(res.data.countries || []))
      .catch(console.error);
  }, [selectedContinent]);

  // Fetch stats
  useEffect(() => {
    axios.get(`${API}/curate/stats`).then((res) => setCuratedStats(res.data)).catch(() => {});
  }, [step]);

  // Generate regions
  const handleGenerateRegions = async () => {
    if (!selectedCountry) return;
    setIsGeneratingRegions(true);
    try {
      const res = await axios.post(`${API}/curate/generate-regions/${encodeURIComponent(selectedCountry)}`);
      setRegions(res.data.regions || []);
      setRegionsCached(res.data.cached || false);
      if (res.data.cached) toast.info("Using cached regions");
      setStep("regions");
    } catch (e) {
      toast.error("Failed: " + (e.response?.data?.detail || e.message));
    }
    setIsGeneratingRegions(false);
  };

  // Clear & regenerate
  const handleClearRegions = async () => {
    try {
      await axios.delete(`${API}/curate/regions/${encodeURIComponent(selectedCountry)}`);
      toast.success("Cleared! Regenerating...");
      setRegions([]);
      setRegionsCached(false);
      handleGenerateRegions();
    } catch (e) {
      toast.error("Failed to clear: " + (e.response?.data?.detail || e.message));
    }
  };

  // Toggle region selection
  const toggleRegion = (region) => {
    setSelectedRegions((prev) =>
      prev.some((r) => r.id === region.id) ? prev.filter((r) => r.id !== region.id) : [...prev, region]
    );
  };

  // Generate beaches for selected regions
  const handleGenerateBeaches = async () => {
    const toGenerate = selectedRegions.length > 0 ? selectedRegions : regions;
    setIsGeneratingBeaches(true);
    setStep("generating");
    setGenerationProgress({ current: 0, total: toGenerate.length, currentName: "" });

    let totalGenerated = 0;
    let allBeaches = [];
    for (let i = 0; i < toGenerate.length; i++) {
      const region = toGenerate[i];
      setGenerationProgress({ current: i + 1, total: toGenerate.length, currentName: region.sub_region_name });

      try {
        const res = await axios.post(`${API}/curate/generate-beaches`, {
          country: selectedCountry,
          sub_region_name: region.sub_region_name,
          boundary_definition: region.boundary_definition || "",
          priority: region.priority || "High",
          region_id: region.id || null,
        });
        const count = res.data.total || 0;
        totalGenerated += count;
        allBeaches = [...allBeaches, ...(res.data.beaches || [])];
        if (res.data.cached) {
          toast.info(`${region.sub_region_name}: ${count} beaches (cached)`);
        } else {
          toast.success(`${region.sub_region_name}: ${count} beaches generated`);
        }
      } catch (e) {
        toast.error(`${region.sub_region_name} failed: ${e.response?.data?.detail || e.message}`);
      }
    }

    toast.success(`Done! ${totalGenerated} total beaches curated for ${selectedCountry}`, { duration: 6000 });
    setIsGeneratingBeaches(false);

    // Refresh regions to get updated counts
    try {
      const res = await axios.get(`${API}/curate/regions/${encodeURIComponent(selectedCountry)}`);
      if (res.data.regions?.length > 0) {
        setRegions(res.data.regions);
      }
    } catch (e) {
      // DB unavailable — update local state with beach counts
      setRegions((prev) =>
        prev.map((r) => {
          const generated = allBeaches.filter((b) => b.region === r.sub_region_name);
          if (generated.length > 0) {
            return { ...r, status: "completed", actual_beach_count: generated.length };
          }
          return r;
        })
      );
    }

    // Store beaches locally for display
    setCuratedBeaches(allBeaches);
    setBeachTotal(allBeaches.length);
    setStep("beaches");
  };

  // Fetch curated beaches (from DB or use locally generated)
  const fetchBeaches = useCallback(async () => {
    try {
      const params = { limit: 50, offset: page * 50 };
      if (selectedCountry) params.country = selectedCountry;
      if (filterRegion) params.region = filterRegion;
      if (filterTier) params.tier = filterTier;
      if (filterTag) params.tag = filterTag;
      if (searchQuery) params.search = searchQuery;
      const res = await axios.get(`${API}/curate/beaches`, { params });
      if (res.data.beaches?.length > 0) {
        setCuratedBeaches(res.data.beaches);
        setBeachTotal(res.data.total || 0);
      }
      // If DB returns empty, keep locally stored beaches from generation
    } catch (e) {
      // DB unavailable — keep locally stored beaches
      console.log("DB unavailable, using locally generated beaches");
    }
  }, [selectedCountry, filterRegion, filterTier, filterTag, searchQuery, page]);

  useEffect(() => {
    if (step === "beaches") {
      // Apply client-side filtering on locally stored beaches
      if (curatedBeaches.length > 0) {
        fetchBeaches(); // Try DB first; if empty, keeps local data
      }
    }
  }, [step, fetchBeaches]);

  // Client-side filtered beaches for display (works without DB)
  const filteredBeaches = curatedBeaches.filter((b) => {
    if (filterRegion && b.region !== filterRegion) return false;
    if (filterTier && b.tier !== Number(filterTier)) return false;
    if (filterTag && !(b.tags || []).includes(filterTag)) return false;
    if (searchQuery && !b.beach_name?.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const displayBeaches = filteredBeaches.slice(page * 50, (page + 1) * 50);
  const displayTotal = filteredBeaches.length;

  return (
    <div className="space-y-6">
      {/* Stats Bar */}
      <div className="flex items-center gap-4">
        <Badge variant="outline" className="border-cyan-500/50 text-cyan-400">{curatedStats.total_beaches?.toLocaleString() || 0} Curated Beaches</Badge>
        <Badge variant="outline" className="border-slate-600">{curatedStats.total_regions || 0} Regions</Badge>
        <Badge variant="outline" className="border-slate-600">{curatedStats.total_countries || 0} Countries</Badge>
        {curatedStats.by_tier?.tier_1 > 0 && <Badge className={TIER_COLORS[1]}>T1: {curatedStats.by_tier.tier_1}</Badge>}
        {curatedStats.by_tier?.tier_2 > 0 && <Badge className={TIER_COLORS[2]}>T2: {curatedStats.by_tier.tier_2}</Badge>}
        {curatedStats.by_tier?.tier_3 > 0 && <Badge className={TIER_COLORS[3]}>T3: {curatedStats.by_tier.tier_3}</Badge>}
      </div>

      {/* Step 1: Country Selection */}
      {(step === "select" || step === "regions") && (
        <Card className="bg-slate-900/50 border-slate-800">
          <CardHeader>
            <CardTitle>Step 1: Select Country</CardTitle>
            <CardDescription>AI will define clean, non-overlapping coastal regions</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Select value={selectedContinent} onValueChange={(v) => { setSelectedContinent(v); setSelectedCountry(""); setRegions([]); setStep("select"); }}>
                <SelectTrigger data-testid="curate-select-continent" className="bg-slate-800 border-slate-700"><SelectValue placeholder="Select continent" /></SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {CONTINENTS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>

              <Select value={selectedCountry} onValueChange={(v) => { setSelectedCountry(v); setRegions([]); setSelectedRegions([]); setStep("select"); }}>
                <SelectTrigger data-testid="curate-select-country" className="bg-slate-800 border-slate-700"><SelectValue placeholder="Select country" /></SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700 max-h-64">
                  {countries.map((c) => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {selectedCountry && step === "select" && (
              <Button data-testid="btn-generate-regions" onClick={handleGenerateRegions} disabled={isGeneratingRegions} className="w-full bg-cyan-600 hover:bg-cyan-500">
                {isGeneratingRegions ? "AI Defining Coastal Regions..." : `Define Regions for ${selectedCountry}`}
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Step 2: Review Regions */}
      {step === "regions" && regions.length > 0 && (
        <Card className="bg-slate-900/50 border-slate-800" data-testid="regions-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Step 2: Review Coastal Regions</CardTitle>
                <CardDescription>
                  {regions.length} regions defined for {selectedCountry}
                  {regionsCached && <Badge variant="outline" className="ml-2 text-amber-400">Cached</Badge>}
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={handleClearRegions} className="border-slate-700 text-xs">Re-generate</Button>
                <Button variant="outline" size="sm" onClick={() => setSelectedRegions(selectedRegions.length === regions.length ? [] : [...regions])} className="border-slate-700 text-xs">
                  {selectedRegions.length === regions.length ? "Clear All" : "Select All"}
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-80">
              <div className="space-y-2">
                {regions.map((r) => {
                  const selected = selectedRegions.some((s) => s.id === r.id);
                  const isDone = r.status === "completed" && r.actual_beach_count > 0;

                  let cardClass = "bg-slate-800/50 hover:bg-slate-700/50 border border-transparent hover:border-slate-600";
                  if (selected) cardClass = "bg-cyan-500/20 border border-cyan-500/50";
                  else if (isDone) cardClass = "bg-emerald-500/10 border border-emerald-500/30";

                  return (
                    <div
                      key={`region-${r.id}`}
                      data-testid={`region-item-${r.id}`}
                      onClick={() => toggleRegion(r)}
                      className={`flex items-start gap-3 p-3 rounded cursor-pointer transition-all ${cardClass}`}
                    >
                      <Checkbox checked={selected} className="pointer-events-none shrink-0 mt-1" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          {isDone && (
                            <svg className="w-4 h-4 text-emerald-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                          <span className={`font-medium ${isDone ? "text-emerald-300" : ""}`}>{r.sub_region_name}</span>
                          <Badge className={`text-[10px] h-4 ${PRIORITY_COLORS[r.priority] || PRIORITY_COLORS.Medium}`}>{r.priority}</Badge>
                        </div>
                        <div className="text-xs text-slate-500">{r.boundary_definition}</div>
                        {r.notes && <div className="text-[10px] text-slate-600 mt-1">{r.notes}</div>}
                        <div className="flex gap-3 mt-1 text-[10px]">
                          <span className="text-slate-500">Est: ~{r.estimated_beach_count}</span>
                          {isDone && <span className="text-emerald-500">Actual: {r.actual_beach_count}</span>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>

            <div className="mt-4 flex items-center justify-between border-t border-slate-800 pt-4">
              <div className="text-sm text-slate-400">
                {selectedRegions.length > 0 ? `${selectedRegions.length} selected` : "All regions will be processed"}
              </div>
              <Button data-testid="btn-generate-beaches" onClick={handleGenerateBeaches} disabled={isGeneratingBeaches} className="bg-emerald-600 hover:bg-emerald-500">
                {isGeneratingBeaches
                  ? "Generating..."
                  : `Generate Beaches (${selectedRegions.length > 0 ? selectedRegions.length : regions.length} regions)`}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Generation Progress */}
      {step === "generating" && (
        <Card className="bg-slate-900/50 border-slate-800">
          <CardHeader><CardTitle>Generating Curated Beaches...</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3">
              <Badge className="bg-cyan-500/20 text-cyan-400">
                {generationProgress.current}/{generationProgress.total}
              </Badge>
              <span className="text-cyan-300">{generationProgress.currentName}</span>
            </div>
            <div className="h-2.5 bg-slate-800 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-cyan-600 to-emerald-400 rounded-full transition-all duration-700"
                style={{ width: `${generationProgress.total > 0 ? (generationProgress.current / generationProgress.total) * 100 : 0}%` }}
              />
            </div>
            <div className="text-xs text-slate-500">AI is curating beaches with tiers, tags, and deduplication...</div>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Browse Curated Beaches */}
      {step === "beaches" && (
        <Card className="bg-slate-900/50 border-slate-800" data-testid="curated-beaches-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Curated Beach Dataset</CardTitle>
                <CardDescription>{displayTotal.toLocaleString()} beaches for {selectedCountry}</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={() => setStep("regions")} className="border-slate-700">Back to Regions</Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Filters */}
            <div className="grid grid-cols-4 gap-3">
              <Select value={filterRegion} onValueChange={(v) => { setFilterRegion(v === "all" ? "" : v); setPage(0); }}>
                <SelectTrigger className="bg-slate-800 border-slate-700"><SelectValue placeholder="Region" /></SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700 max-h-48">
                  <SelectItem value="all">All Regions</SelectItem>
                  {[...new Set(curatedBeaches.map((b) => b.region))].sort().map((r) => (
                    <SelectItem key={`fr-${r}`} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filterTier} onValueChange={(v) => { setFilterTier(v === "all" ? "" : v); setPage(0); }}>
                <SelectTrigger className="bg-slate-800 border-slate-700"><SelectValue placeholder="Tier" /></SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  <SelectItem value="all">All Tiers</SelectItem>
                  <SelectItem value="1">Tier 1 - Iconic</SelectItem>
                  <SelectItem value="2">Tier 2 - Regional</SelectItem>
                  <SelectItem value="3">Tier 3 - Hidden Gems</SelectItem>
                </SelectContent>
              </Select>

              <Select value={filterTag} onValueChange={(v) => { setFilterTag(v === "all" ? "" : v); setPage(0); }}>
                <SelectTrigger className="bg-slate-800 border-slate-700"><SelectValue placeholder="Tag" /></SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700 max-h-48">
                  <SelectItem value="all">All Tags</SelectItem>
                  {["famous","hidden","surf","party","family","couples","snorkeling","diving","scenic","white_sand","turquoise_water","dog_friendly","sunset","island","urban"].map((t) => (
                    <SelectItem key={t} value={t}>{t.replace("_"," ")}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Input
                placeholder="Search by name..."
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
                className="bg-slate-800 border-slate-700"
              />
            </div>

            {/* Results */}
            <div className="flex items-center justify-between">
              <Badge variant="outline" className="border-cyan-500/50 text-cyan-400">{displayTotal.toLocaleString()} results</Badge>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)} className="border-slate-700">Prev</Button>
                <span className="text-sm text-slate-400 self-center">Page {page + 1}</span>
                <Button variant="outline" size="sm" disabled={displayBeaches.length < 50} onClick={() => setPage((p) => p + 1)} className="border-slate-700">Next</Button>
              </div>
            </div>

            <ScrollArea className="h-96">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-800">
                    <TableHead className="text-slate-400 w-16">Tier</TableHead>
                    <TableHead className="text-slate-400">Beach</TableHead>
                    <TableHead className="text-slate-400">Region</TableHead>
                    <TableHead className="text-slate-400">City</TableHead>
                    <TableHead className="text-slate-400">Tags</TableHead>
                    <TableHead className="text-slate-400">Why it Matters</TableHead>
                    <TableHead className="text-slate-400 w-24">Coords</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {displayBeaches.map((b) => (
                    <TableRow key={`cb-${b.id}-${b.beach_name}`} className="border-slate-800">
                      <TableCell>
                        <Badge className={`text-[10px] ${TIER_COLORS[b.tier] || TIER_COLORS[2]}`}>
                          {TIER_LABELS[b.tier] || `T${b.tier}`}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-medium">{b.beach_name}</TableCell>
                      <TableCell className="text-sm text-slate-400">{b.region}</TableCell>
                      <TableCell className="text-sm text-slate-400">{b.city || "-"}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-0.5">
                          {(b.tags || []).slice(0, 3).map((t) => (
                            <Badge key={`t-${b.id}-${t}`} variant="outline" className="text-[9px] h-4 px-1">{t}</Badge>
                          ))}
                          {(b.tags || []).length > 3 && <Badge variant="outline" className="text-[9px] h-4 px-1">+{b.tags.length - 3}</Badge>}
                        </div>
                      </TableCell>
                      <TableCell className="text-xs text-slate-500 max-w-[200px] truncate">{b.why_it_matters || "-"}</TableCell>
                      <TableCell className="font-mono text-[10px] text-slate-500">{b.latitude?.toFixed(3)}, {b.longitude?.toFixed(3)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
